import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { useData } from '../../hooks/useData';
import { useToast } from '../../hooks/useToast';
import { nowISO } from '../../utils/helpers';
import { knowledgeLibraryServices } from '../../APIServices/knowledgeLibraryServices';
import { knowledgeBaseMetadataServices } from '../../APIServices/knowledgeBaseMetadataServices';

function KnowledgeUploadModal({ open, onClose }) {
  const { loadKnowledgeList, audit, currentUser } = useData();
  const toast = useToast();
  const [typeOptions, setTypeOptions] = useState([]);
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [therapeuticOptions, setTherapeuticOptions] = useState([]);
  const [form, setForm] = useState({
    title: '',
    type: 'Scientific paper',
    source: '',
    molecule: '',
    therapeutic: 'Cardiovascular',
    region: 'Global',
    year: new Date().getFullYear(),
    tags: '',
    excerpt: '',
    content: '',
    collection: 'literature',
    confidentiality: 'Internal'
  });

  const fallbackTypeOptions = useMemo(() => ([
    { label: 'Scientific paper', value: 'Scientific paper' },
    { label: 'Regulatory guidance', value: 'Regulatory guidance' },
    { label: 'Product information', value: 'Product information' },
    { label: 'Historical feasibility', value: 'Historical feasibility' },
    { label: 'Lessons learned', value: 'Lessons learned' },
  ]), []);

  const fallbackCollectionOptions = useMemo(() => ([
    { label: 'Scientific literature', value: 'literature' },
    { label: 'Regulatory guidance', value: 'regulatory' },
    { label: 'Product / drug', value: 'product' },
    { label: 'Arcolab historical', value: 'historical' },
  ]), []);

  const fallbackTherapeuticOptions = useMemo(() => ([
    { label: 'Cardiovascular', value: 'Cardiovascular' },
    { label: 'Endocrinology', value: 'Endocrinology' },
    { label: 'Oncology', value: 'Oncology' },
    { label: 'CNS', value: 'CNS' },
    { label: 'Respiratory', value: 'Respiratory' },
    { label: 'Multi', value: 'Multi' },
    { label: 'All', value: 'All' },
  ]), []);

  useEffect(() => {
    if (!open) return;

    const loadMetadata = async () => {
      const [typeData, collectionData, therapeuticData] = await Promise.all([
        knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData('active', 'knowledgebase_type'),
        knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData('active', 'knowledgebase_collection'),
        knowledgeBaseMetadataServices.getKnowledgeBaseMetadataData('active', 'knowledgebase_therapeutic_area'),
      ]);

      const mapRows = (rows) =>
        (rows || []).map((row) => ({
          label: row.label,
          value: row.value || row.label,
        }));

      const typeRows = mapRows(typeData?.data);
      const collectionRows = mapRows(collectionData?.data);
      const therapeuticRows = mapRows(therapeuticData?.data);

      setTypeOptions(typeRows);
      setCollectionOptions(collectionRows);
      setTherapeuticOptions(therapeuticRows);
    };

    loadMetadata();
  }, [open]);

  const resolvedTypeOptions = typeOptions.length ? typeOptions : fallbackTypeOptions;
  const resolvedCollectionOptions = collectionOptions.length ? collectionOptions : fallbackCollectionOptions;
  const resolvedTherapeuticOptions = therapeuticOptions.length ? therapeuticOptions : fallbackTherapeuticOptions;

  useEffect(() => {
    if (!resolvedTypeOptions.find((o) => o.value === form.type)) {
      set('type', resolvedTypeOptions[0]?.value || 'Scientific paper');
    }
    if (!resolvedCollectionOptions.find((o) => o.value === form.collection)) {
      set('collection', resolvedCollectionOptions[0]?.value || 'literature');
    }
    if (!resolvedTherapeuticOptions.find((o) => o.value === form.therapeutic)) {
      set('therapeutic', resolvedTherapeuticOptions[0]?.value || 'Cardiovascular');
    }
  }, [
    resolvedTypeOptions,
    resolvedCollectionOptions,
    resolvedTherapeuticOptions,
    form.type,
    form.collection,
    form.therapeutic,
  ]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title) {
      toast('Title is required', 'warn');
      return;
    }
    const payload = {
      ...form,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      status: 'review',
      uploadedAt: nowISO(),
      uploadedBy: currentUser?.id,
      pages: Math.floor(Math.random() * 30 + 8)
    };

    const data = await knowledgeLibraryServices.addNewKnowledgeLibraryItem(payload);
    const item = data?.data;
    await loadKnowledgeList();
    audit('KNOWLEDGE_UPLOAD', form.title, `Collection: ${form.collection}`, { knowledgeId: item?.id || item?.knowledgeId });
    toast(`Uploaded "${form.title}" — pending approval`, 'ok');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Upload knowledge item"
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>
            <i className="ti ti-upload" /> Upload & start indexing
          </button>
        </>
      }
    >
      <div className="xs muted mb-3">
        <i className="ti ti-info-circle" /> Uploads start as "under review". A knowledge owner must approve before the item becomes available to the deterministic engine.
      </div>

      <div className="field">
        <label className="field-label">Title <span className="req">*</span></label>
        <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Apixaban Bioequivalence Study Review 2024" />
      </div>

      <div className="field-row">
        <div className="field">
          <label className="field-label">Type</label>
          <select value={form.type} onChange={(e) => set('type', e.target.value)}>
            {resolvedTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Collection</label>
          <select value={form.collection} onChange={(e) => set('collection', e.target.value)}>
            {resolvedCollectionOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row-3">
        <div className="field">
          <label className="field-label">Molecule</label>
          <input value={form.molecule} onChange={(e) => set('molecule', e.target.value)} />
        </div>
        <div className="field">
          <label className="field-label">Source / authority</label>
          <input value={form.source} onChange={(e) => set('source', e.target.value)} placeholder="e.g. CDSCO, NEJM" />
        </div>
        <div className="field">
          <label className="field-label">Year</label>
          <input type="number" value={form.year} onChange={(e) => set('year', e.target.value)} />
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label className="field-label">Therapeutic area</label>
          <select value={form.therapeutic} onChange={(e) => set('therapeutic', e.target.value)}>
            {resolvedTherapeuticOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="field-label">Region</label>
          <select value={form.region} onChange={(e) => set('region', e.target.value)}>
            <option>Global</option>
            <option>India</option>
            <option>USA</option>
            <option>EU</option>
          </select>
        </div>
      </div>

      <div className="field">
        <label className="field-label">Tags (comma-separated)</label>
        <input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="CV%, food-effect, BABE" />
      </div>

      <div className="field">
        <label className="field-label">Excerpt / abstract (1–2 sentences for search preview)</label>
        <textarea value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} rows={2} />
      </div>

      <div className="field">
        <label className="field-label">Full text (indexed for search)</label>
        <textarea value={form.content} onChange={(e) => set('content', e.target.value)} rows={6} placeholder="In production, this is auto-extracted from uploaded PDFs (with OCR for scanned documents). Paste key text here for prototype." />
      </div>
    </Modal>
  );
}

export default KnowledgeUploadModal;